import {
	Card,
	CardContent,
	IconButton,
	Paper,
	Typography,
	Tooltip,
} from '@mui/material';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as HelpQuestion } from 'common/assets/images/help-question.svg';
import { Loader } from 'common/components/Loader/Loader';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	OBLIGATION_TABLE_DEFAULT_FILTER,
	refreshTimeout,
	RISK_COUNT_MAIN_LABEL,
} from 'common/constants/TaxonomyConstants';
import { NO_DATA_FOUND } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setRiskRefTaxonomyModalFlag,
	setRiskRefTaxonomySourceFiltersKeyFlag,
} from 'common/store/reducer/taxonomyReducer';
import { fixedTwoDecimalOnlyPositiveValue } from 'common/utils/utilFunctions';

import { getTaxonomyHeatMapData } from '../../api';
import {
	IGetTaxonomyHeatMapParams,
	IHeatMapDetails,
	IHeatMapViewContent,
	IRiskRefHeatMapProps,
} from '../../api/type-def';
import RiskModal from '../../RiskModal/RiskModal';

import { getBorderColorClass } from './GetBorderColorClass';
import HeatMapViewPagination from './HeatMapViewPagination';
import { ReturnTrendWithIcon } from './ReturnTrendWithIcon';

import styles from './RiskTaxonomyHeatMap.module.scss';

const { OK } = HTTP_STATUS_CODES;

const { imageColor } = styles;
const renderHeatMapCards = (items: IHeatMapViewContent[]) =>
	items?.map((category) => (
		<div className={styles['cards-group-container']} key={uuidv4()}>
			<div className={styles['catagory-name']}>
				<h3>
					{category.section} ({category.details.length})
				</h3>
			</div>
			<div className={styles['card-groups']}>
				{category.details.map((item: IHeatMapDetails) => (
					<Card
						data-testid="heat-card-testId"
						key={uuidv4()}
						className={styles['card-box']}
						sx={{
							borderLeft: `${getBorderColorClass(item?.bandColor)}`,
						}}
					>
						<CardContent className={styles['card-content']}>
							<Tooltip title={item?.level} placement="bottom">
								<div className={styles.title}>{item?.level}</div>
							</Tooltip>
							<div className={styles['score-box-divider']}>&nbsp;</div>
							<div className={styles['score-box']}>
								<div className={styles['score-box-divider']}>&nbsp;</div>
								<div className={styles['score-box-trend']}>
									{fixedTwoDecimalOnlyPositiveValue(item.heatMapScore ?? '0')}
									{ReturnTrendWithIcon(item.heatMapScoreChange ?? '0')}
								</div>
							</div>
						</CardContent>
					</Card>
				))}
			</div>
		</div>
	));

const RiskTaxonomyHeatMap = (props: IRiskRefHeatMapProps) => {
	const { isClearFilterValues, selectedTabIndex } = props;
	const dispatch = useDispatch();
	const defaultTableQueryParams = {
		pageNumber: 1,
	};
	const [heatMapData, setHeatMapData] = useState<IHeatMapViewContent[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [totalPagesCount, setTotalPagesCount] = useState<number>(0);
	const timerRef = useRef<any>(null);
	const riskRefTaxonomySourceFiltersKey = useSelector(
		(state: RootState) => state.taxonomy.riskRefTaxonomySourceFiltersKey,
	);
	const searchText = useSelector(
		(state: RootState) => state.taxonomy.riskRefSearchText,
	);
	const applyFilterFlag = useSelector(
		(state: RootState) => state.taxonomy.applyFilterFlag,
	);

	const riskRefTaxonomySourceFiltersKeyFlag = useSelector(
		(state: RootState) => state.taxonomy.riskRefTaxonomySourceFiltersKeyFlag,
	);
	const tabIndex = useSelector(
		(state: RootState) => state.common.selectedTabName,
	);
	const refFilterData = useSelector(
		(state: RootState) => state.taxonomy.riskRefTaxonomyFilters,
	);

	const fetchHeatMapData = async (
		apiData: IGetTaxonomyHeatMapParams,
		pollingFlg?: boolean,
	) => {
		const filter =
			selectedTabIndex === 3
				? [...refFilterData, ...OBLIGATION_TABLE_DEFAULT_FILTER]
				: refFilterData;

		const paramsObj = {
			index: tabIndex,
			source: riskRefTaxonomySourceFiltersKey,
			filter,
			searchText,
		};
		if (!pollingFlg) setHeatMapData([]);
		setIsLoading(true);
		const response = await getTaxonomyHeatMapData(paramsObj, apiData);
		if (response?.statusCode === OK) {
			const responseData = response?.responseBody;
			const tempTotalPageCount = responseData?.totalPages || 0;
			const tempHeatMapData =
				responseData?.heatMapViewResponseWrapper?.content &&
				responseData?.heatMapViewResponseWrapper?.content?.length
					? responseData.heatMapViewResponseWrapper?.content
					: [];
			if (tempHeatMapData.length > 0) {
				setHeatMapData(tempHeatMapData);
				setTotalPagesCount(tempTotalPageCount);
			} else {
				setHeatMapData([]);
			}
			setIsLoading(false);
		} else {
			setIsLoading(false);
		}
	};

	const openRiskModal = () => {
		dispatch(setRiskRefTaxonomyModalFlag(true));
	};
	const handlePageChange = (data: IGetTaxonomyHeatMapParams) => {
		fetchHeatMapData(data);
		defaultTableQueryParams.pageNumber = data.pageNumber;
		handleSetInterval();
	};
	const handleClearInterval = () => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
	};
	const handleSetInterval = () => {
		handleClearInterval();
		const timer = setInterval(() => {
			fetchHeatMapData(defaultTableQueryParams, true);
		}, refreshTimeout);
		timerRef.current = timer;
	};
	const resetDefault = () => {
		setTotalPagesCount(0);
		if (defaultTableQueryParams.pageNumber > 0)
			defaultTableQueryParams.pageNumber -= 1;
		fetchHeatMapData(defaultTableQueryParams);
		handleSetInterval();
	};
	useEffect(() => {
		if (applyFilterFlag) {
			resetDefault();
		}
	}, [applyFilterFlag]);
	useEffect(() => {
		if (riskRefTaxonomySourceFiltersKeyFlag) {
			resetDefault();
			dispatch(setRiskRefTaxonomySourceFiltersKeyFlag(false));
		}
	}, [riskRefTaxonomySourceFiltersKey]);
	useEffect(() => {
		resetDefault();
		return handleClearInterval;
	}, [searchText, selectedTabIndex]);
	useEffect(() => {
		if (isClearFilterValues) {
			resetDefault();
		}
	}, [isClearFilterValues]);
	const renderHeatMapCardsItems = useMemo(
		() => renderHeatMapCards(heatMapData),
		[heatMapData],
	);
	const renderConditionalHeatMapCards = () =>
		heatMapData.length === 0 ? (
			<Paper className={styles['common-table-box']}>{NO_DATA_FOUND}</Paper>
		) : (
			renderHeatMapCardsItems
		);
	return (
		<div className={styles['risk-taxonomy-heatmap']}>
			<div className={styles['heatmap-legend']}>
				<IconButton
					className={styles['heatmap-helpicon']}
					onClick={() => {
						openRiskModal();
					}}
					disableRipple
				>
					<HelpQuestion fill={imageColor} />

					<Typography className={styles['heatmap-helptext']}>
						{RISK_COUNT_MAIN_LABEL}
					</Typography>
				</IconButton>
			</div>
			{isLoading && heatMapData.length === 0 ? (
				<div className={styles['loader-cont']}>
					<Loader data-testid="loader" show />
				</div>
			) : (
				renderConditionalHeatMapCards()
			)}
			{totalPagesCount !== 0 && (
				<HeatMapViewPagination
					defaultTableQueryParams={defaultTableQueryParams}
					totalPagesCount={totalPagesCount}
					handlePageChange={handlePageChange}
				/>
			)}
			<RiskModal />
		</div>
	);
};

export default RiskTaxonomyHeatMap;
